<div class="container-fluid py-3 py-md-4 manager-timesheets-page">
  <div class="card shadow-sm border-0 mb-3">
    <div class="card-body">
      <div
        class="d-flex align-items-center justify-content-between gap-2 mb-3 print-hide"
      >
        <h4 class="mb-0">Manage Timesheets</h4>
        <button
          type="button"
          class="btn btn-outline-primary btn-sm"
          [disabled]="!hasEmployeeSelected || isLoadingEntries"
          (click)="printTimesheet()"
        >
          <i class="bi bi-printer me-2"></i>Print
        </button>
      </div>

      <div class="print-only print-header mb-3" *ngIf="hasEmployeeSelected">
        <h4 class="mb-1">Timesheet</h4>
        <div><strong>Employee:</strong> {{ selectedEmployeeName }}</div>
        <div><strong>Pay Period:</strong> {{ periodLabel }}</div>
        <div>
          <strong>Total Time Worked:</strong>
          {{ payPeriodTotal | number: "1.2-2" }} hrs
        </div>
      </div>

      <div class="row g-3 align-items-end controls-row print-hide">
        <div class="col-12 col-lg-4">
          <label class="form-label fw-semibold" for="employeeSelect"
            >Employee</label
          >
          <select
            id="employeeSelect"
            class="form-select"
            [disabled]="isLoadingEmployees"
            [(ngModel)]="selectedEmployeeId"
            (change)="onEmployeeChange()"
          >
            <option value="">Select employee...</option>
            <option *ngFor="let employee of employees" [value]="employee.id">
              {{ employee.displayName }}
            </option>
          </select>
        </div>

        <div class="col-12 col-lg-4">
          <label class="form-label fw-semibold">Pay Period</label>
          <div class="d-flex align-items-center gap-2 period-nav">
            <button
              class="btn btn-outline-secondary btn-sm"
              type="button"
              (click)="goToPreviousPeriod()"
              [disabled]="isLoadingEntries"
              aria-label="Previous pay period"
            >
              <i class="bi bi-chevron-left"></i>
            </button>

            <div class="period-label fw-semibold text-center">
              {{ periodLabel }}
            </div>

            <button
              class="btn btn-outline-secondary btn-sm"
              type="button"
              (click)="goToNextPeriod()"
              [disabled]="isLoadingEntries"
              aria-label="Next pay period"
            >
              <i class="bi bi-chevron-right"></i>
            </button>
          </div>
        </div>

        <div class="col-12 col-lg-4">
          <label class="form-label fw-semibold" for="payRate"
            >Pay Rate ($/hr)</label
          >
          <input
            id="payRate"
            class="form-control"
            type="number"
            min="0"
            step="0.01"
            [ngModel]="payRate"
            (ngModelChange)="onPayRateInput($event)"
            placeholder="0.00"
          />
          <small class="text-muted d-block mt-2">
            Estimated Pay: {{ estimatedPay | currency: "USD" }}
          </small>
        </div>
      </div>

      <div
        *ngIf="errorMessage"
        class="alert alert-danger mt-3 mb-0 py-2 print-hide"
      >
        {{ errorMessage }}
      </div>
    </div>
  </div>

  <div class="card shadow-sm border-0">
    <div class="card-body p-0 p-md-3">
      <div
        *ngIf="!hasEmployeeSelected"
        class="alert alert-info m-3 mb-0 print-hide"
      >
        Select an employee to view and manage time entries for this pay period.
      </div>

      <div class="table-responsive" *ngIf="hasEmployeeSelected">
        <table class="table table-hover align-middle mb-0">
          <thead class="table-light">
            <tr>
              <th>Day</th>
              <th>Date</th>
              <th>Time In</th>
              <th>Time Out</th>
              <th>Hours</th>
              <th>Note</th>
              <th class="text-end print-hide">Actions</th>
            </tr>
          </thead>
          <tbody>
            <ng-container
              *ngFor="let group of groups; let i = index; trackBy: trackByDate"
            >
              <ng-container
                *ngIf="group.entries.length === 0; else dayWithEntries"
              >
                <tr [class.today-group]="group.isToday">
                  <td class="fw-semibold">{{ group.dayLabel }}</td>
                  <td>{{ group.dateLabel }}</td>
                  <td>—</td>
                  <td>—</td>
                  <td>—</td>
                  <td>—</td>
                  <td class="text-end print-hide">—</td>
                </tr>

                <tr class="day-total-row" [class.today-group]="group.isToday">
                  <td colspan="6" class="text-end small fw-semibold">
                    Total: {{ group.dayTotal | number: "1.2-2" }} hrs
                  </td>
                  <td class="text-end print-hide">
                    <button
                      type="button"
                      class="btn btn-link btn-sm p-0"
                      (click)="openManualEntryModal(group)"
                    >
                      + Add
                    </button>
                  </td>
                </tr>
              </ng-container>

              <ng-template #dayWithEntries>
                <ng-container
                  *ngFor="
                    let entry of group.entries;
                    let first = first;
                    let idx = index
                  "
                >
                  <tr [class.today-group]="group.isToday">
                    <td
                      *ngIf="first"
                      [attr.rowspan]="group.rowSpan"
                      class="fw-semibold"
                    >
                      {{ group.dayLabel }}
                    </td>
                    <td *ngIf="first" [attr.rowspan]="group.rowSpan">
                      {{ group.dateLabel }}
                    </td>
                    <td>{{ formatTime(entry.clockIn) }}</td>
                    <td>
                      {{ entry.clockOut ? formatTime(entry.clockOut) : "—" }}
                    </td>
                    <td>
                      {{
                        entry.hoursWorked !== null
                          ? (entry.hoursWorked | number: "1.2-2")
                          : "—"
                      }}
                    </td>
                    <td>{{ entry.note || "—" }}</td>
                    <td class="text-end print-hide">
                      <button
                        type="button"
                        class="btn btn-sm btn-outline-secondary"
                        (click)="openEditEntryModal(entry)"
                        aria-label="Edit entry"
                      >
                        <i class="bi bi-pencil"></i>
                      </button>
                    </td>
                  </tr>

                  <tr
                    *ngIf="getGapNote(group, idx) as gap"
                    class="gap-note-row"
                    [class.today-group]="group.isToday"
                  >
                    <td colspan="5" class="text-muted small fst-italic ps-3">
                      {{ gap.label }}
                    </td>
                  </tr>
                </ng-container>

                <tr class="day-total-row" [class.today-group]="group.isToday">
                  <td colspan="6" class="text-end small fw-semibold">
                    Total: {{ group.dayTotal | number: "1.2-2" }} hrs
                  </td>
                  <td class="text-end print-hide">
                    <button
                      type="button"
                      class="btn btn-link btn-sm p-0"
                      (click)="openManualEntryModal(group)"
                    >
                      + Add
                    </button>
                  </td>
                </tr>
              </ng-template>

              <tr *ngIf="i === 6" class="week-subtotal-row">
                <td colspan="7" class="text-end fw-semibold">
                  Week Subtotal: {{ week1Subtotal | number: "1.2-2" }} hrs
                </td>
              </tr>

              <tr *ngIf="i === 13" class="week-subtotal-row">
                <td colspan="7" class="text-end fw-semibold">
                  Week Subtotal: {{ week2Subtotal | number: "1.2-2" }} hrs
                </td>
              </tr>
            </ng-container>
          </tbody>
          <tfoot>
            <tr class="table-secondary">
              <td colspan="5" class="text-end fw-bold">Pay Period Total</td>
              <td class="fw-bold">
                {{ payPeriodTotal | number: "1.2-2" }} hrs
              </td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  </div>
</div>

<div
  #manualEntryModal
  class="modal fade"
  id="managerManualEntryModal"
  tabindex="-1"
  aria-labelledby="managerManualEntryModalLabel"
  aria-hidden="true"
>
  <div class="modal-dialog modal-dialog-centered">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title" id="managerManualEntryModalLabel">
          Add Manual Entry
        </h5>
        <button
          type="button"
          class="btn-close"
          data-bs-dismiss="modal"
          aria-label="Close"
        ></button>
      </div>

      <form [formGroup]="manualEntryForm" (ngSubmit)="submitManualEntry()">
        <div class="modal-body">
          <p class="mb-3 text-muted">{{ selectedManualDateLabel }}</p>

          <div class="row g-3">
            <div class="col-6">
              <label class="form-label" for="manualTimeIn">Time In</label>
              <input
                id="manualTimeIn"
                type="time"
                class="form-control"
                formControlName="timeIn"
              />
            </div>

            <div class="col-6">
              <label class="form-label" for="manualTimeOut">Time Out</label>
              <input
                id="manualTimeOut"
                type="time"
                class="form-control"
                formControlName="timeOut"
              />
            </div>

            <div class="col-12">
              <label class="form-label" for="manualNote">Note (optional)</label>
              <textarea
                id="manualNote"
                rows="3"
                class="form-control"
                formControlName="note"
                placeholder="Optional note"
              ></textarea>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-outline-secondary"
            data-bs-dismiss="modal"
            [disabled]="manualSaving"
          >
            Cancel
          </button>

          <button
            type="submit"
            class="btn btn-primary"
            [disabled]="manualSaving"
          >
            <span
              *ngIf="manualSaving"
              class="spinner-border spinner-border-sm me-2"
              aria-hidden="true"
            ></span>
            Save Entry
          </button>
        </div>
      </form>
    </div>
  </div>
</div>

<div
  #editEntryModal
  class="modal fade"
  id="editEntryModal"
  tabindex="-1"
  aria-labelledby="editEntryModalLabel"
  aria-hidden="true"
>
  <div class="modal-dialog modal-dialog-centered">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title" id="editEntryModalLabel">Edit Time Entry</h5>
        <button
          type="button"
          class="btn-close"
          data-bs-dismiss="modal"
          aria-label="Close"
        ></button>
      </div>

      <form [formGroup]="editEntryForm" (ngSubmit)="saveEditedEntry()">
        <div class="modal-body">
          <div class="row g-3">
            <div class="col-6">
              <label class="form-label" for="editTimeIn">Time In</label>
              <input
                id="editTimeIn"
                type="time"
                class="form-control"
                formControlName="timeIn"
              />
            </div>

            <div class="col-6">
              <label class="form-label" for="editTimeOut">Time Out</label>
              <input
                id="editTimeOut"
                type="time"
                class="form-control"
                formControlName="timeOut"
              />
            </div>

            <div class="col-12">
              <label class="form-label" for="editNote">Note</label>
              <textarea
                id="editNote"
                rows="3"
                class="form-control"
                formControlName="note"
              ></textarea>
            </div>
          </div>

          <div
            *ngIf="showDeleteConfirm"
            class="alert alert-warning mt-3 mb-0 py-2"
          >
            <div class="d-flex flex-column gap-2">
              <span>Delete this entry?</span>
              <div class="d-flex gap-2">
                <button
                  type="button"
                  class="btn btn-sm btn-danger"
                  [disabled]="deleteSaving"
                  (click)="confirmDeleteEntry()"
                >
                  <span
                    *ngIf="deleteSaving"
                    class="spinner-border spinner-border-sm me-2"
                    aria-hidden="true"
                  ></span>
                  Yes, Delete
                </button>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-secondary"
                  [disabled]="deleteSaving"
                  (click)="showDeleteConfirm = false"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer justify-content-between">
          <button
            type="button"
            class="btn btn-danger"
            [disabled]="editSaving || deleteSaving"
            (click)="showDeleteConfirm = true"
          >
            Delete
          </button>

          <div class="d-flex gap-2">
            <button
              type="button"
              class="btn btn-outline-secondary"
              data-bs-dismiss="modal"
              [disabled]="editSaving || deleteSaving"
            >
              Close
            </button>

            <button
              type="submit"
              class="btn btn-primary"
              [disabled]="editSaving || deleteSaving"
            >
              <span
                *ngIf="editSaving"
                class="spinner-border spinner-border-sm me-2"
                aria-hidden="true"
              ></span>
              Save Changes
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
</div>
